import React, { useEffect, useState } from "react";
import {
  FiFileText, FiShoppingBag, FiShare2, FiMail, FiTarget, FiZap, FiUsers, FiEdit3, FiFeather,
  FiDownload, FiTrash2, FiLoader, FiInfo, FiCopy, FiCheck, FiRefreshCw,
} from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { useJobHistory, formatSeconds } from "./audioShared";

const ICONS = { blog: FiFileText, product: FiShoppingBag, social: FiShare2, email: FiMail, ad: FiTarget, slogan: FiZap, about: FiUsers, rewrite: FiEdit3, custom: FiFeather };
const LENGTHS = [["short", "Short"], ["medium", "Medium"], ["long", "Long"]];
const inputClass = "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white text-sm placeholder-gray-500 focus:outline-none focus:border-accent-light";

function download(filename, text) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
}

function CopyButton({ getText, label }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => { if (await copyText(getText())) { setDone(true); setTimeout(() => setDone(false), 1800); } }}
      className="px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors"
    >
      {done ? <><FiCheck className="text-emerald-400" /> Copied</> : <><FiCopy /> {label}</>}
    </button>
  );
}

// **bold** inside a line
function Inline({ text }) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith("**") && part.endsWith("**") ? <strong key={i} className="text-white">{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>));
}

// The written text: # headings, "- " bullets, "1. " numbered lines and paragraphs; everything else stays as written.
function RenderedText({ text }) {
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  text.split("\n").forEach((line, i) => {
    const heading = line.match(/^(#{1,3})\s+(.*)/);
    const bullet = line.match(/^\s*[-*•]\s+(.*)/);
    const numbered = line.match(/^\s*(\d{1,2})[.)]\s+(.*)/);
    if (heading) {
      flush();
      const level = heading[1].length;
      blocks.push({ type: "h", level, text: heading[2], key: i });
    } else if (bullet || numbered) {
      const ordered = !!numbered;
      if (!list || list.ordered !== ordered) { flush(); list = { type: "list", ordered, items: [], key: i }; }
      list.items.push((bullet || numbered)[bullet ? 1 : 2]);
    } else if (!line.trim()) {
      flush();
    } else {
      flush();
      blocks.push({ type: "p", text: line, key: i });
    }
  });
  flush();
  return (
    <div className="space-y-3 text-gray-100 leading-relaxed">
      {blocks.map((b) => {
        if (b.type === "h") return <p key={b.key} className={`font-bold text-white ${b.level === 1 ? "text-2xl" : "text-lg mt-2"}`}><Inline text={b.text} /></p>;
        if (b.type === "list") {
          const Tag = b.ordered ? "ol" : "ul";
          return <Tag key={b.key} className={`${b.ordered ? "list-decimal" : "list-disc"} ps-6 space-y-1.5 marker:text-accent-light`}>{b.items.map((item, j) => <li key={j}><Inline text={item} /></li>)}</Tag>;
        }
        return <p key={b.key}><Inline text={b.text} /></p>;
      })}
    </div>
  );
}

function Field({ field, value, onChange }) {
  const common = { id: `cg-${field.key}`, value, onChange: (e) => onChange(e.target.value), className: inputClass, placeholder: field.placeholder || "" };
  return (
    <div className={field.kind === "textarea" ? "md:col-span-2" : ""}>
      <label htmlFor={common.id} className="block text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">
        {field.label}{field.required && <span className="text-accent-light"> *</span>}
      </label>
      {field.kind === "textarea" ? (
        <textarea {...common} rows={field.key === "brief" && field.max > 5000 ? 9 : 4} maxLength={field.max || 4000} className={`${inputClass} resize-y`} />
      ) : field.kind === "select" ? (
        <select {...common}>{field.options.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
      ) : (
        <input {...common} maxLength={200} />
      )}
    </div>
  );
}

// AI Text and Content Generation: pick what to write, fill a short brief, get the text. The page draws its form from
// the server's definitions (/core/content/types/); the writing runs in the background and the page polls for it.
export default function ContentGeneratorTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("content");
  const [config, setConfig] = useState(null);
  const [typeKey, setTypeKey] = useState("product");
  const [values, setValues] = useState({});
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [language, setLanguage] = useState("en");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    apiRequest("/core/content/types/").then(({ ok, data }) => { if (ok) setConfig(data); });
  }, []);

  useEffect(() => {
    if (latest && latest.status === "completed" && latest.has_transcript) setViewId(latest.id);
  }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => { if (alive) setDetail(ok ? data : null); });
    return () => { alive = false; };
  }, [viewId, clientId]);

  const type = config?.types.find((t) => t.key === typeKey);
  const missing = type ? type.fields.filter((f) => f.required && !(values[f.key] || "").trim()) : [];
  const canSubmit = type && missing.length === 0 && !submitting && !running;

  const setValue = (key) => (value) => setValues((prev) => ({ ...prev, [key]: value }));

  const send = async (payload) => {
    setError("");
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "content");
    Object.entries(payload).forEach(([k, v]) => form.append(k, Array.isArray(v) ? v.join("\n") : v ?? ""));
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) return setError(data?.brief || errorMessage(data));
    setViewId(null);
    add(data);
  };

  const submit = () => {
    const defaults = Object.fromEntries(type.fields.filter((f) => f.kind === "select").map((f) => [f.key, f.default]));
    return send({ type: typeKey, ...defaults, ...values, tone, length, language });
  };

  const again = () => { try { send(JSON.parse(detail.options)); } catch { setError("Could not repeat that request."); } };

  const extra = detail?.segments && !Array.isArray(detail.segments) ? detail.segments : {};
  const arabicShare = detail ? (detail.transcript.match(/[؀-ۿ]/g) || []).length / Math.max(1, detail.transcript.length) : 0;
  const rtl = arabicShare > 0.3;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Text and Content Generation</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Write blog posts, product descriptions, social posts, emails, ads, slogans and more in minutes. Choose what to write,
        add a few facts, pick the tone and language, and get a ready-to-use draft.
      </p>

      {!config ? (
        <p className="text-center text-gray-400">Loading...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {config.types.map((t) => {
              const Icon = ICONS[t.key] || FiFeather;
              const active = t.key === typeKey;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => { setTypeKey(t.key); setValues({}); setError(""); }}
                  className={`text-left rounded-xl border p-4 transition-colors ${active ? "border-accent-light bg-accent/10" : "border-navy-border bg-white/5 hover:border-accent-light/60"}`}
                >
                  <span className="flex items-center gap-2.5 font-semibold text-sm">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-brand-gradient" : "bg-white/10"}`}><Icon size={15} /></span>
                    {t.label}
                  </span>
                  <span className="block text-xs text-gray-400 mt-2 leading-snug">{t.description}</span>
                </button>
              );
            })}
          </div>

          {type && (
            <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
              <div className="grid md:grid-cols-2 gap-5">
                {type.fields.map((field) => (
                  <Field key={`${typeKey}-${field.key}`} field={field} value={values[field.key] ?? field.default ?? ""} onChange={setValue(field.key)} />
                ))}
              </div>

              <div className="grid md:grid-cols-3 gap-5 mt-6 pt-6 border-t border-navy-border">
                <div>
                  <label htmlFor="cg-tone" className="block text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">Tone</label>
                  <select id="cg-tone" className={inputClass} value={tone} onChange={(e) => setTone(e.target.value)}>
                    {config.tones.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                {type.uses_length && (
                  <div>
                    <span className="block text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">Length</span>
                    <div className="flex gap-2">
                      {LENGTHS.map(([key, label]) => (
                        <button key={key} type="button" onClick={() => setLength(key)}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${length === key ? "bg-accent-light text-navy" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <label htmlFor="cg-language" className="block text-xs uppercase tracking-widest text-white/50 font-semibold mb-2">Write in</label>
                  <select id="cg-language" className={inputClass} value={language} onChange={(e) => setLanguage(e.target.value)}>
                    {config.languages.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6">{error}</p>}

          <button
            onClick={submit}
            disabled={!canSubmit}
            className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <><FiLoader className="animate-spin" /> Sending...</> : running ? <><FiLoader className="animate-spin" /> Writing...</> : <><FiFeather /> Write it</>}
          </button>
          {!canSubmit && !running && !submitting && missing.length > 0 && <p className="text-xs text-gray-500 text-center mt-2">Fill in &ldquo;{missing[0].label}&rdquo; first</p>}
          <p className="text-xs text-gray-500 text-center mt-3">
            Your brief is sent to an AI writing service to produce the text, so leave out confidential information. Always read the result before you use it.
          </p>
        </>
      )}

      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div>
            <p className="font-semibold">Writing... {formatSeconds(elapsed)}</p>
            <p className="text-sm text-gray-400">A short text takes about 10 to 40 seconds; a long article or a busy moment can take a minute or two. You can stay on this page.</p>
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>{latest.error_message || "The text could not be written. Please try again."}</span>
        </div>
      )}

      {detail && detail.status === "completed" && !running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">Your text</p>
              <p className="font-semibold truncate">{detail.original_name}</p>
              {extra.stats?.words > 0 && <p className="text-xs text-gray-400 mt-0.5">{extra.stats.words.toLocaleString()} words</p>}
            </div>
            {detail.engine && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">{detail.engine}</span>}
          </div>
          {extra.notice && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-4">{extra.notice}</p>}
          <div className="rounded-xl bg-navy border border-navy-border p-5" dir={rtl ? "rtl" : "ltr"}>
            <RenderedText text={detail.transcript} />
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <CopyButton getText={() => detail.transcript} label="Copy text" />
            <button type="button" onClick={() => download(`${detail.original_name.replace(/[^\w]+/g, "-").slice(0, 40) || "text"}.txt`, detail.transcript)}
              className="px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors">
              <FiDownload /> Download .txt
            </button>
            <button type="button" onClick={again} disabled={submitting || !!running}
              className="px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-50">
              <FiRefreshCw /> Write another version
            </button>
          </div>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your texts</p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing here yet. Your texts will appear here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border p-4 ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                <p className="text-sm text-gray-200 truncate">{job.original_name}</p>
                <p className="text-xs text-gray-500 mb-3">{new Date(job.created_at).toLocaleString()} · {job.status === "completed" ? "Ready" : job.status === "failed" ? "Failed" : "Writing..."}</p>
                <div className="flex items-center justify-between gap-3">
                  {job.status === "completed" && job.has_transcript ? (
                    <button type="button" onClick={() => setViewId(job.id)} className="text-sm text-accent-light hover:underline">View text</button>
                  ) : job.status === "failed" ? <p className="text-xs text-red-300">{job.error_message}</p> : <span />}
                  {job.status !== "processing" && job.status !== "queued" && (
                    <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
